import { GameCardDetail } from './GamesApi'

export interface SourceImage { displayUrl: string; originalUrl: string; filename: string }
export interface GameSourcePerson {
  personId: string; name: string; details: GameCardDetail[]; images: SourceImage[]
  childOrder?: number; siblingsCount?: number; childCount?: number
}

// FamilySearch platform APIs, called directly with a user-supplied token — this is a
// throwaway authoring tool, not part of the app's own auth flow. Read-person endpoint
// and headers mirror the backend FSReader (fhx: FHX.ResearchData.FS/FSReader.cs).
const FS_API_BASE = 'https://api.familysearch.org'
const GEDCOMX_ACCEPT = 'application/x-gedcomx-v1+json'

// Conclusions that FS models as facts but that aren't life events — they carry the
// name, gender, and living flag surfaced elsewhere, so they don't belong in the list.
const SUPPRESSED_FACT_TYPES = new Set([
  'http://gedcomx.org/Name',
  'http://gedcomx.org/Gender',
  'http://gedcomx.org/Living',
])

const COUPLE_TYPE = 'http://gedcomx.org/Couple'

// Filename extensions an <img> can decode. Other memories (PDF/TIFF/HEIC) are shown and uploaded
// via FS's rasterized preview instead of their raw file, which an <img> renders as a blank box.
const BROWSER_RENDERABLE = /\.(jpe?g|png|gif|webp|bmp|avif)$/i

interface FsDate { original?: string; formal?: string }
interface FsPlace { original?: string }
interface FsFact { type?: string; date?: FsDate; place?: FsPlace; value?: string }
interface FsPerson {
  id?: string
  display?: { name?: string }
  names?: { nameForms?: { fullText?: string }[] }[]
  facts?: FsFact[]
}
interface FsReference { resourceId?: string }
interface FsRelationship {
  type?: string
  person1?: FsReference
  person2?: FsReference
  facts?: FsFact[]
}
// A parent-child edge (from the /parents and /children responses): parent1 is the father, parent2
// the mother, keyed by resourceId -- mirrors the backend FSChild.cs mapping.
interface FsChildAndParents { child?: FsReference; parent1?: FsReference; parent2?: FsReference }
interface FsPersonsResponse {
  persons?: FsPerson[]
  relationships?: FsRelationship[]
  childAndParentsRelationships?: FsChildAndParents[]
}

interface FsLink { href?: string }
interface FsSourceDescription {
  links?: Record<string, FsLink | undefined>
  artifactMetadata?: { filename?: string }[]
}
interface FsMemoriesResponse {
  sourceDescriptions?: FsSourceDescription[]
  links?: { next?: { href?: string } }
}

interface CoupleEvents {
  spousePid?: string
  facts: FsFact[]
}

// Reads a FamilySearch person into a plain GameSourcePerson — the person's name, the
// merged life-event details (own facts + couple/marriage facts), and a deduped image
// gallery covering the person and every spouse. No Vue refs and no framing state:
// choosing and cropping images is the editor's job.
export async function readGameSourcePerson(personId: string, token: string): Promise<GameSourcePerson> {
  const data = await fetchPersonData(personId, token)
  const person = selectPerson(data, personId)
  if (!person) {
    throw new Error('No person found in the response.')
  }
  const resolvedId = person.id ?? personId
  const name = personName(person)

  const couples = couplesFor(data, resolvedId)
  const spousePids = [...new Set(couples.map(c => c.spousePid).filter((p): p is string => !!p))]
  const spouseNames = await readSpouseNames(spousePids, token)

  const details = buildDetails(person.facts ?? [], couples, spouseNames)

  // Gallery covers the person and each spouse; sibling order and child count come from the person's
  // parent/child edges. All are independent reads, so fetch them together.
  const [images, childCount, siblingOrder] = await Promise.all([
    gatherImages([resolvedId, ...spousePids], token),
    readChildCount(resolvedId, token).catch(() => undefined),
    readSiblingOrder(resolvedId, token).catch(() => ({})),
  ])

  return { personId: resolvedId, name, details, images, childCount, ...siblingOrder }
}

// Reads just the memory gallery for a person and each spouse, without the person's facts -- lets the
// editor fill the image picker for an FS-sourced card without re-reading (and clobbering) its details.
export async function readGameSourceImages(personId: string, token: string): Promise<SourceImage[]> {
  const data = await fetchPersonData(personId, token)
  const person = selectPerson(data, personId)
  if (!person) {
    throw new Error('No person found in the response.')
  }
  const resolvedId = person.id ?? personId
  const spousePids = [...new Set(couplesFor(data, resolvedId).map(c => c.spousePid).filter((p): p is string => !!p))]
  return gatherImages([resolvedId, ...spousePids], token)
}

// Fetches and dedups memory images across the given person ids (person + spouses).
async function gatherImages(pids: string[], token: string): Promise<SourceImage[]> {
  const imageGroups = await Promise.all(pids.map(p => fetchMemoryImages(p, token).catch(() => [])))
  const seen = new Set<string>()
  return imageGroups.flat()
    .filter(image => {
      if (seen.has(image.originalUrl)) {
        return false
      }
      seen.add(image.originalUrl)
      return true
    })
    .map(image => ({ displayUrl: image.displayUrl, originalUrl: image.originalUrl, filename: image.filename }))
}

// The /parents and /children endpoints each return childAndParentsRelationships (the parent-child
// edges) plus the related persons carrying birth facts. 204 means no relatives of that kind; a null
// return means the read failed (counts are then left undefined rather than reported as zero).
async function fetchRelatives(pid: string, relation: 'parents' | 'children', token: string): Promise<FsPersonsResponse | null> {
  try {
    const response = await fetch(`${FS_API_BASE}/platform/tree/persons/${encodeURIComponent(pid)}/${relation}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: GEDCOMX_ACCEPT },
    })
    if (response.status === 204) {
      return {}
    }
    if (!response.ok) {
      return null
    }
    return await response.json() as FsPersonsResponse
  } catch {
    return null
  }
}

// The person's own children across all marriages -- the /children edges where the person is a parent.
async function readChildCount(pid: string, token: string): Promise<number | undefined> {
  const data = await fetchRelatives(pid, 'children', token)
  if (!data) {
    return undefined
  }
  const childIds = new Set(
    (data.childAndParentsRelationships ?? [])
      .filter(edge => edge.parent1?.resourceId === pid || edge.parent2?.resourceId === pid)
      .map(edge => edge.child?.resourceId)
      .filter((id): id is string => !!id),
  )
  return childIds.size
}

// The person's birth order among full siblings: read the parents to find the parent family, then that
// family's children ordered by birth. childOrder is 1-based; siblingsCount includes the person.
async function readSiblingOrder(pid: string, token: string): Promise<{ childOrder?: number; siblingsCount?: number }> {
  const parents = await fetchRelatives(pid, 'parents', token)
  const ownEdge = (parents?.childAndParentsRelationships ?? []).find(edge => edge.child?.resourceId === pid)
  const fatherId = ownEdge?.parent1?.resourceId
  const motherId = ownEdge?.parent2?.resourceId
  const parentId = fatherId ?? motherId
  if (!parentId) {
    return {}
  }
  const siblingsData = await fetchRelatives(parentId, 'children', token)
  if (!siblingsData) {
    return {}
  }
  const personsById = new Map<string, FsPerson>()
  for (const person of siblingsData.persons ?? []) {
    if (person.id) {
      personsById.set(person.id, person)
    }
  }
  // Full siblings share both parents (or the one known parent, for a single-parent family).
  const sameFamily = (edge: FsChildAndParents) =>
    fatherId && motherId
      ? edge.parent1?.resourceId === fatherId && edge.parent2?.resourceId === motherId
      : fatherId
        ? edge.parent1?.resourceId === fatherId
        : edge.parent2?.resourceId === motherId
  const siblingIds = [...new Set(
    (siblingsData.childAndParentsRelationships ?? [])
      .filter(sameFamily)
      .map(edge => edge.child?.resourceId)
      .filter((id): id is string => !!id),
  )]
  siblingIds.sort((a, b) => birthSortKey(personsById.get(a)) - birthSortKey(personsById.get(b)))
  const index = siblingIds.indexOf(pid)
  if (index < 0) {
    return {}
  }
  return { childOrder: index + 1, siblingsCount: siblingIds.length }
}

function birthSortKey(person: FsPerson | undefined): number {
  const birth = person?.facts?.find(fact => fact.type === 'http://gedcomx.org/Birth')
  return dateSortKey(birth?.date?.formal ?? '')
}

async function fetchPersonData(pid: string, token: string): Promise<FsPersonsResponse> {
  const response = await fetch(`${FS_API_BASE}/platform/tree/persons/${encodeURIComponent(pid)}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: GEDCOMX_ACCEPT },
  })
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`)
  }
  return await response.json() as FsPersonsResponse
}

function selectPerson(data: FsPersonsResponse, pid: string) {
  return data.persons?.find(p => p.id === pid) ?? data.persons?.[0]
}

function personName(person: FsPerson) {
  return person.display?.name
    ?? person.names?.[0]?.nameForms?.[0]?.fullText
    ?? ''
}

// Reads each spouse to resolve their name — best effort, blank if a read fails.
async function readSpouseNames(spousePids: string[], token: string): Promise<Map<string, string>> {
  const names = new Map<string, string>()
  await Promise.all(spousePids.map(async spousePid => {
    const data = await fetchPersonData(spousePid, token).catch(() => null)
    const spouse = data ? selectPerson(data, spousePid) : undefined
    names.set(spousePid, spouse ? personName(spouse) : '')
  }))
  return names
}

// Marriage (and other couple events) live on the Couple relationship's facts, not on
// the person, so pull those in too — pairing each couple with the spouse (the other partner).
function couplesFor(data: FsPersonsResponse, personId: string): CoupleEvents[] {
  return (data.relationships ?? [])
    .filter(r => r.type === COUPLE_TYPE
      && (r.person1?.resourceId === personId || r.person2?.resourceId === personId))
    .map(r => ({
      spousePid: r.person1?.resourceId === personId ? r.person2?.resourceId : r.person1?.resourceId,
      facts: r.facts ?? [],
    }))
}

function buildDetails(personFacts: FsFact[], couples: CoupleEvents[], spouseNames: Map<string, string>): GameCardDetail[] {
  const details: GameCardDetail[] = []
  for (const fact of personFacts) {
    const detail = factToDetail(fact)
    if (detail) {
      details.push(detail)
    }
  }
  for (const couple of couples) {
    const spouse = couple.spousePid ? spouseNames.get(couple.spousePid) : undefined
    for (const fact of couple.facts) {
      const detail = factToDetail(fact, spouse)
      if (detail) {
        details.push(detail)
      }
    }
  }
  return details.sort((a, b) => dateSortKey(a.date ?? '') - dateSortKey(b.date ?? ''))
}

// The API's GameCardDetail has no spouse field, so a couple event's spouse name is folded
// into value: it becomes value when value is empty, otherwise it's appended after a separator.
function factToDetail(fact: FsFact, spouse?: string): GameCardDetail | null {
  if (fact.type && SUPPRESSED_FACT_TYPES.has(fact.type)) {
    return null
  }
  const type = factTypeLabel(fact.type)
  if (!type) {
    return null
  }
  const detail: GameCardDetail = {
    date: fact.date?.formal ?? '',
    type,
  }
  if (fact.value) {
    detail.value = fact.value
  }
  if (spouse) {
    detail.value = detail.value ? `${detail.value} · ${spouse}` : spouse
  }
  if (fact.place?.original) {
    detail.place = fact.place.original
  }
  return detail
}

function factTypeLabel(typeUri: string | undefined) {
  if (!typeUri) {
    return ''
  }
  // Custom FS fact types are a data URI carrying a form-encoded label, e.g.
  // "data:,Set+Apart+for+Mission" or "data:,Military%20Discharge" -- decode to a readable label.
  if (typeUri.startsWith('data:,')) {
    const encoded = typeUri.slice('data:,'.length).replace(/\+/g, ' ')
    try {
      return decodeURIComponent(encoded).trim()
    } catch {
      return encoded.trim()
    }
  }
  const segment = typeUri.split('/').pop() ?? ''
  // "MilitaryService" -> "Military Service"; leaves single-word types untouched.
  return segment.replace(/([a-z])([A-Z])/g, '$1 $2')
}

function dateSortKey(formal: string) {
  // GedcomX formal dates look like +1917-12-20 (ranges/approximations carry a prefix
  // or a second date, so match the first year-month-day and sort undated events last).
  const match = /([+-])(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/.exec(formal)
  if (!match) {
    return Number.MAX_SAFE_INTEGER
  }
  const sign = match[1] === '-' ? -1 : 1
  const year = Number(match[2])
  const month = Number(match[3] ?? '0')
  const day = Number(match[4] ?? '0')
  return sign * (year * 10000 + month * 100 + day)
}

// Photos attached to the person via the Memories API. Per the FS docs, an image memory's
// sourceDescription carries image-thumbnail / image-icon / image / artifact link rels
// (mediaType is often absent, so the image link is the reliable signal); non-image memories
// have none. Displayed with the token in the query string: <img> rendering isn't subject to
// CORS, and FS accepts access_token as a query param for authenticated GETs. /memories 204s
// when empty. Returns a dedup-key rel plus the largest displayable rel — the tile renders that
// one, so right-click "save image" gets a full-size image, not a thumbnail.
// /memories is paged (25 per page); follow the `next` link, capped at 4 pages (100 memories).
async function fetchMemoryImages(pid: string, token: string): Promise<SourceImage[]> {
  const images: SourceImage[] = []
  let url: string | undefined = `${FS_API_BASE}/platform/tree/persons/${encodeURIComponent(pid)}/memories`
  for (let page = 0; url && page < 4; page++) {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, Accept: GEDCOMX_ACCEPT },
    })
    if (!response.ok || response.status === 204) {
      break
    }
    const data = await response.json() as FsMemoriesResponse
    for (const sd of data.sourceDescriptions ?? []) {
      const renderable = BROWSER_RENDERABLE.test(memoryFilename(sd))
      const display = memoryDisplayHref(sd, renderable)
      if (display) {
        // The uploaded card image must be a raster an <img> can decode: the full image for a photo,
        // or FS's rasterized preview for a non-image memory (its raw `image` rel is the PDF/TIFF itself).
        const original = renderable ? (sd.links?.image?.href ?? display) : display
        images.push({ displayUrl: withToken(display, token), originalUrl: withToken(original, token), filename: memoryFilename(sd) })
      }
    }
    const next = data.links?.next?.href
    const nextUrl = next ? new URL(next, FS_API_BASE).href : undefined
    url = nextUrl && nextUrl !== url ? nextUrl : undefined
  }
  return images
}

// The URL the <img> renders. For a format the browser can decode, use the full `image` rel; but
// that rel serves the source file as-is, so for a PDF it's the PDF and for TIFF/HEIC it's the raw
// (undecodable) image — an <img> shows a blank box for those. Those fall back to FS's rasterized
// JPEG preview instead. We tell which case we're in from the filename extension.
function memoryDisplayHref(sd: FsSourceDescription, renderable: boolean) {
  if (renderable) {
    return sd.links?.image?.href
      ?? sd.links?.['image-icon']?.href
      ?? sd.links?.['image-thumbnail']?.href
  }
  return sd.links?.['image-thumbnail']?.href
    ?? sd.links?.['image-icon']?.href
    ?? sd.links?.image?.href
}

// The original upload's filename, e.g. "alma-mission.jpg" — used to save the file and to name
// the card's image path (the URL itself carries no usable name — right-click just gives dist.jpg).
function memoryFilename(sd: FsSourceDescription) {
  return sd.artifactMetadata?.find(meta => meta.filename)?.filename ?? ''
}

function withToken(href: string, token: string) {
  const separator = href.includes('?') ? '&' : '?'
  return `${href}${separator}access_token=${encodeURIComponent(token)}`
}
